import { Injectable, signal, computed, effect, inject } from '@angular/core';

type ThemePreference = 'light' | 'dark' | 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themePreference = signal<ThemePreference>(
    (localStorage.getItem('theme') as ThemePreference) || 'system'
  );

  private readonly systemPrefersDark = signal<boolean>(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  readonly theme = computed<'light' | 'dark'>(() => {
    const pref = this.themePreference();
    return pref === 'system'
      ? this.systemPrefersDark() ? 'dark' : 'light'
      : pref;
  });

  readonly isDark = computed(() => this.theme() === 'dark');

  constructor() {
    // Apply initial theme
    this.applyTheme(this.theme());

    // React to any theme change
    effect(() => {
      const currentPref = this.themePreference();
      const resolvedTheme = this.theme();

      localStorage.setItem('theme', currentPref);
      this.applyTheme(resolvedTheme);
    });

    // Watch system preference changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      this.systemPrefersDark.set(e.matches);
    });
  }

  setTheme(theme: ThemePreference) {
    this.themePreference.set(theme);
  }

  toggleTheme() {
    const current = this.themePreference();
    const next: ThemePreference =
      current === 'dark' ? 'light' :
      current === 'light' ? 'system' :
      'dark';

    this.setTheme(next);
  }

  getThemePreference(): ThemePreference {
    return this.themePreference();
  }

  private applyTheme(mode: 'light' | 'dark') {
    document.documentElement.classList.toggle('dark', mode === 'dark');
  }
}
